import clsx from "clsx";

import { progressLine } from "../lib/format";

export type ProgressBandProps = {
  found: number;
  total: number;
  /** "trees" — comes from the collection's rankPlural, never guessed. */
  plural: string;
  /** Finds that weren't on the list. */
  bonusCount?: number;
  className?: string;
  /** Hide the sentence and show only the band. */
  bandOnly?: boolean;
};

const MAX_CELLS = 24;

/**
 * Progress as a tally, not a percentage.
 *
 * Small lists get one cell per target — a ticked box for each thing found,
 * which is exactly what a checklist in the back of a field guide looks like.
 * Long lists fall back to a single ruled bar.
 */
export function ProgressBand({
  found,
  total,
  plural,
  bonusCount = 0,
  className,
  bandOnly = false,
}: ProgressBandProps) {
  const safeTotal = Math.max(0, total);
  const safeFound = Math.min(safeTotal, Math.max(0, found));
  const fraction = safeTotal === 0 ? 0 : safeFound / safeTotal;
  const label = progressLine(safeFound, safeTotal, plural);

  return (
    <div className={className}>
      {safeTotal > 0 && safeTotal <= MAX_CELLS ? (
        <div
          role="img"
          aria-label={label}
          className="flex items-stretch gap-[3px]"
        >
          {Array.from({ length: safeTotal }, (_, index) => (
            <span
              key={index}
              className={clsx(
                "h-2.5 flex-1 border",
                index < safeFound ? "border-teal bg-teal" : "border-rule bg-transparent",
              )}
            />
          ))}
        </div>
      ) : (
        <div
          role="img"
          aria-label={label}
          className="h-2.5 w-full overflow-hidden border border-rule bg-transparent"
        >
          <span
            className="block h-full bg-teal transition-[width] duration-500 ease-out"
            style={{ width: `${Math.round(fraction * 100)}%` }}
          />
        </div>
      )}

      {bandOnly ? null : (
        <p className="mt-2 flex flex-wrap items-baseline gap-x-2 font-label text-xs tracking-wide text-ink-soft uppercase">
          <span className="text-ink">{label}</span>
          {bonusCount > 0 ? (
            <span className="text-ochre">
              + {bonusCount} bonus {bonusCount === 1 ? "find" : "finds"}
            </span>
          ) : null}
        </p>
      )}
    </div>
  );
}
